export type Language = 'en' | 'pt' | 'es' | 'fr' | 'de';

export interface Translations {
  // Header
  nav: {
    home: string;
    about: string;
    contact: string;
    apps: string;
  };
  
  // Home Page
  home: {
    hero: {
      titleHighlight: string;
      tagline: string;
      subtitle: string;
      exploreApps: string;
      learnMore: string;
    };
    games: {
      title: string;
      subtitle: string;
      motivators: {
        title: string;
        description: string;
        cta: string;
      };
      poker: {
        title: string;
        description: string;
        cta: string;
      };
    };
    why: {
      title: string;
      subtitle: string;
      engaging: {
        title: string;
        description: string;
      };
      collaborative: {
        title: string;
        description: string;
      };
      instant: {
        title: string;
        description: string;
      };
    };
    cta: {
      title: string;
      subtitle: string;
      button: string;
    };
  };

  // About Page
  about: {
    hero: {
      title: string;
      tagline: string;
    };
    story: {
      title: string;
      subtitle: string;
      content: string[];
    };
    pillars: {
      title: string;
      subtitle: string;
    };
    mission: {
      title: string;
      description: string;
    };
    vision: {
      title: string;
      description: string;
    };
    values: {
      title: string;
      description: string;
    };
    drives: {
      title: string;
      collaboration: {
        title: string;
        description: string;
      };
      fun: {
        title: string;
        description: string;
      };
      growth: {
        title: string;
        description: string;
      };
      learning: {
        title: string;
        description: string;
      };
    };
  };

  // Contact Page
  contact: {
    hero: {
      title: string;
      tagline: string;
    };
    ways: {
      title: string;
      subtitle: string;
    };
    methods: {
      email: {
        title: string;
        description: string;
      };
      support: {
        title: string;
        description: string;
        value: string;
      };
      community: {
        title: string;
        description: string;
        value: string;
      };
    };
    form: {
      title: string;
      subtitle: string;
      name: string;
      namePlaceholder: string;
      email: string;
      emailPlaceholder: string;
      message: string;
      messagePlaceholder: string;
      submit: string;
    };
  };

  // App Launcher
  apps: {
    hero: {
      title: string;
      tagline: string;
    };
    available: {
      title: string;
      subtitle: string;
    };
    title: string;
    subtitle: string;
    noApps: string;
  };
}

export const translations: Record<Language, Translations> = {
  en: {
    nav: {
      home: 'Home',
      about: 'About',
      contact: 'Contact',
      apps: 'Apps',
    },
    home: {
      hero: {
        titleHighlight: 'Squadron Play',
        tagline: 'The Ultimate Gaming & Collaboration Hub',
        subtitle: 'Experience incredible games designed to make teamwork fun, decision-making easier, and team building memorable. No downloads needed—just jump in, play, and have fun!',
        exploreApps: 'Play Now',
        learnMore: 'Learn More',
      },
      games: {
        title: '🎮 Discover Our Games',
        subtitle: 'Engage your team with interactive games designed for collaboration and growth',
        motivators: {
          title: 'Moving Motivators',
          description: 'Align your team around what truly motivates them. Perfect for understanding team values, building trust, and creating a shared vision.',
          cta: 'Play Moving Motivators',
        },
        poker: {
          title: 'Delegation Poker',
          description: 'Master decision-making with engaging voting on who should handle decisions. Learn who owns what and boost team autonomy.',
          cta: 'Play Delegation Poker',
        },
      },
      why: {
        title: '✨ Why Squadron Play?',
        subtitle: 'Everything you need to build stronger, more aligned teams',
        engaging: {
          title: 'Engaging & Fun',
          description: 'Games that are genuinely enjoyable, making team building and learning feel less like work and more like play.',
        },
        collaborative: {
          title: 'Team Focused',
          description: 'Designed specifically for teams to interact, discuss, and align on what matters most.',
        },
        instant: {
          title: 'No Setup Required',
          description: 'Play instantly online with your team. No downloads, no complicated setup—just start playing.',
        },
      },
      cta: {
        title: 'Ready to Build a Better Team?',
        subtitle: 'Start playing with your team today and discover what makes them tick.',
        button: 'Explore Games',
      },
    },
    about: {
      hero: {
        title: 'About Squadron Play',
        tagline: 'Creating the Ultimate Gaming & Collaboration Hub',
      },
      story: {
        title: 'Our Story',
        subtitle: 'How it all began',
        content: [
          'Squadron Play was born from a simple idea: teams need better ways to collaborate, communicate, and connect. We believed that gaming could be more than just entertainment—it could be a powerful tool for building stronger relationships and fostering meaningful collaboration.',
          'Our founders recognized that traditional team-building exercises often felt forced and ineffective. They envisioned a platform where games became a natural bridge between teammates, enabling authentic conversations and genuine connections.',
          'Today, Squadron Play stands as a testament to the power of playful collaboration. We continue to innovate and create experiences that bring teams together, making work more enjoyable and teams more connected.',
        ],
      },
      pillars: {
        title: 'The Foundation of Squadron Play',
        subtitle: 'What drives us forward',
      },
      mission: {
        title: '🎯 Mission',
        description: 'To create engaging and collaborative gaming experiences that strengthen team bonds and communication.',
      },
      vision: {
        title: '🚀 Vision',
        description: 'A world where teams connect through fun, meaningful games that foster collaboration and growth.',
      },
      values: {
        title: '💎 Values',
        description: 'Integrity, innovation, and inclusivity in everything we do.',
      },
      drives: {
        title: 'What Drives Us',
        collaboration: {
          title: '🤝 Collaboration',
          description: 'We believe great things happen when people work together and truly understand each other.',
        },
        fun: {
          title: '😊 Fun',
          description: 'Work should be enjoyable. We create experiences that bring joy and laughter to the workplace.',
        },
        growth: {
          title: '📈 Growth',
          description: 'Every game is an opportunity to learn, develop new skills, and improve as a team.',
        },
        learning: {
          title: '🎓 Learning',
          description: 'We\'re committed to continuous improvement and helping teams discover new ways to succeed together.',
        },
      },
    },
    contact: {
      hero: {
        title: 'Get in Touch',
        tagline: 'We\'d love to hear from you and answer any questions you might have',
      },
      ways: {
        title: 'Ways to Reach Us',
        subtitle: 'Choose the best way to connect with our team',
      },
      methods: {
        email: {
          title: 'Email Us',
          description: 'Send us an email anytime',
        },
        support: {
          title: 'Live Support',
          description: 'Chat with our support team',
          value: 'Available 9 AM - 6 PM EST',
        },
        community: {
          title: 'Join Community',
          description: 'Connect with other users',
          value: 'Discord & Forums',
        },
      },
      form: {
        title: 'Send Us a Message',
        subtitle: 'Fill out the form below and we\'ll get back to you shortly',
        name: 'Name',
        namePlaceholder: 'Your name',
        email: 'Email',
        emailPlaceholder: 'your.email@example.com',
        message: 'Message',
        messagePlaceholder: 'Tell us how we can help...',
        submit: 'Send Message',
      },
    },
    apps: {
      hero: {
        title: 'Applications',
        tagline: 'Explore our collection of interactive games and tools for teams',
      },
      available: {
        title: 'Available Games',
        subtitle: 'Select a game to start playing with your team',
      },
      title: 'Applications',
      subtitle: 'Browse our collection of apps and games',
      noApps: 'No applications available at the moment.',
    },
  },
  pt: {
    nav: {
      home: 'Início',
      about: 'Sobre',
      contact: 'Contato',
      apps: 'Apps',
    },
    home: {
      hero: {
        titleHighlight: 'Squadron Play',
        tagline: 'O Maior Hub de Jogos e Colaboração em Equipe',
        subtitle: 'Experimente jogos incríveis projetados para tornar o trabalho em equipe divertido, a tomada de decisões mais fácil e o desenvolvimento de equipe memorável. Sem downloads—apenas entre, jogue e divirta-se!',
        exploreApps: 'Começar a Jogar',
        learnMore: 'Saiba Mais',
      },
      games: {
        title: '🎮 Descubra Nossos Jogos',
        subtitle: 'Engaje sua equipe com jogos interativos projetados para colaboração e crescimento',
        motivators: {
          title: 'Moving Motivators',
          description: 'Alinhe sua equipe em torno do que realmente os motiva. Perfeito para entender os valores da equipe, construir confiança e criar uma visão compartilhada.',
          cta: 'Jogar Moving Motivators',
        },
        poker: {
          title: 'Delegation Poker',
          description: 'Domine a tomada de decisões com votação engajante sobre quem deve lidar com as decisões. Aprenda quem é responsável pelo quê e impulsione a autonomia da equipe.',
          cta: 'Jogar Delegation Poker',
        },
      },
      why: {
        title: '✨ Por que Squadron Play?',
        subtitle: 'Tudo que você precisa para construir equipes mais fortes e alinhadas',
        engaging: {
          title: 'Envolvente & Divertido',
          description: 'Jogos genuinamente agradáveis que tornam o desenvolvimento de equipe e aprendizado menos como trabalho e mais como diversão.',
        },
        collaborative: {
          title: 'Focado em Equipe',
          description: 'Projetado especificamente para equipes interagirem, discutirem e se alinharem no que é mais importante.',
        },
        instant: {
          title: 'Sem Configuração Necessária',
          description: 'Jogue instantaneamente online com sua equipe. Sem downloads, sem configuração complicada—apenas comece a jogar.',
        },
      },
      cta: {
        title: 'Pronto para Construir uma Equipe Melhor?',
        subtitle: 'Comece a jogar com sua equipe hoje e descubra o que os motiva realmente.',
        button: 'Explorar Jogos',
      },
    },
    about: {
      hero: {
        title: 'Sobre o Squadron Play',
        tagline: 'Criando o Maior Hub de Jogos e Colaboração em Equipe',
      },
      story: {
        title: 'Nossa História',
        subtitle: 'Como tudo começou',
        content: [
          'Squadron Play nasceu de uma ideia simples: as equipes precisam de melhores maneiras de colaborar, se comunicar e se conectar. Acreditamos que jogos poderiam ser muito mais do que apenas entretenimento—poderiam ser uma ferramenta poderosa para construir relacionamentos mais fortes e fomentar colaboração significativa.',
          'Nossos fundadores reconheceram que os exercícios tradicionais de team building muitas vezes pareciam forçados e ineficazes. Eles vislumbraram uma plataforma onde os jogos se tornassem uma ponte natural entre colegas de equipe, permitindo conversas autênticas e conexões genuínas.',
          'Hoje, Squadron Play é um testemunho do poder da colaboração lúdica. Continuamos inovando e criando experiências que aproximam as equipes, tornando o trabalho mais agradável e as equipes mais conectadas.',
        ],
      },
      pillars: {
        title: 'A Fundação do Squadron Play',
        subtitle: 'O que nos motiva a avançar',
      },
      mission: {
        title: '🎯 Missão',
        description: 'Criar experiências de jogos envolventes e colaborativos que fortaleçam os laços de equipe e a comunicação.',
      },
      vision: {
        title: '🚀 Visão',
        description: 'Um mundo onde as equipes se conectam através de jogos divertidos e significativos que fomentam colaboração e crescimento.',
      },
      values: {
        title: '💎 Valores',
        description: 'Integridade, inovação e inclusividade em tudo o que fazemos.',
      },
      drives: {
        title: 'O Que Nos Motiva',
        collaboration: {
          title: '🤝 Colaboração',
          description: 'Acreditamos que grandes coisas acontecem quando as pessoas trabalham juntas e realmente se entendem.',
        },
        fun: {
          title: '😊 Diversão',
          description: 'O trabalho deve ser agradável. Criamos experiências que trazem alegria e risada para o ambiente de trabalho.',
        },
        growth: {
          title: '📈 Crescimento',
          description: 'Cada jogo é uma oportunidade de aprender, desenvolver novas habilidades e melhorar como equipe.',
        },
        learning: {
          title: '🎓 Aprendizado',
          description: 'Estamos comprometidos com a melhoria contínua e ajudar as equipes a descobrir novas formas de sucesso juntas.',
        },
      },
    },
    contact: {
      hero: {
        title: 'Entre em Contato',
        tagline: 'Adoraríamos ouvir de você e responder todas as suas perguntas',
      },
      ways: {
        title: 'Formas de Nos Contatar',
        subtitle: 'Escolha a melhor forma de se conectar com nossa equipe',
      },
      methods: {
        email: {
          title: 'Envie um E-mail',
          description: 'Nos envie um e-mail a qualquer momento',
        },
        support: {
          title: 'Suporte ao Vivo',
          description: 'Converse com nossa equipe de suporte',
          value: 'Disponível 9h - 18h BRT',
        },
        community: {
          title: 'Participe da Comunidade',
          description: 'Conecte-se com outros usuários',
          value: 'Discord & Fóruns',
        },
      },
      form: {
        title: 'Envie uma Mensagem',
        subtitle: 'Preencha o formulário abaixo e retornaremos em breve',
        name: 'Nome',
        namePlaceholder: 'Seu nome',
        email: 'E-mail',
        emailPlaceholder: 'seu.email@exemplo.com',
        message: 'Mensagem',
        messagePlaceholder: 'Conte-nos como podemos ajudar...',
        submit: 'Enviar Mensagem',
      },
    },
    apps: {
      hero: {
        title: 'Aplicações',
        tagline: 'Explore nossa coleção de jogos e ferramentas interativas para equipes',
      },
      available: {
        title: 'Jogos Disponíveis',
        subtitle: 'Selecione um jogo para começar a jogar com sua equipe',
      },
      title: 'Aplicações',
      subtitle: 'Navegue por nossa coleção de aplicativos e jogos',
      noApps: 'Nenhuma aplicação disponível no momento.',
    },
  },
  es: {
    nav: {
      home: 'Inicio',
      about: 'Acerca',
      contact: 'Contacto',
      apps: 'Apps',
    },
    home: {
      hero: {
        titleHighlight: 'Squadron Play',
        tagline: 'El Mejor Hub de Juegos y Colaboración en Equipo',
        subtitle: 'Experimenta juegos increíbles diseñados para hacer el trabajo en equipo divertido, la toma de decisiones más fácil y el desarrollo de equipo memorable. Sin descargas—simplemente entra, juega y ¡diviértete!',
        exploreApps: 'Comenzar a Jugar',
        learnMore: 'Saber Más',
      },
      games: {
        title: '🎮 Descubre Nuestros Juegos',
        subtitle: 'Involucra a tu equipo con juegos interactivos diseñados para colaboración y crecimiento',
        motivators: {
          title: 'Moving Motivators',
          description: 'Alinea tu equipo en torno a lo que realmente los motiva. Perfecto para entender los valores del equipo, construir confianza y crear una visión compartida.',
          cta: 'Jugar Moving Motivators',
        },
        poker: {
          title: 'Delegation Poker',
          description: 'Domina la toma de decisiones con votación atractiva sobre quién debe manejar las decisiones. Aprende quién es responsable de qué e impulsa la autonomía del equipo.',
          cta: 'Jugar Delegation Poker',
        },
      },
      why: {
        title: '✨ ¿Por Qué Squadron Play?',
        subtitle: 'Todo lo que necesitas para construir equipos más fuertes y alineados',
        engaging: {
          title: 'Atractivo y Divertido',
          description: 'Juegos genuinamente agradables que hacen que la formación de equipo y el aprendizaje se sientan menos como trabajo y más como diversión.',
        },
        collaborative: {
          title: 'Enfocado en Equipo',
          description: 'Diseñado específicamente para que los equipos interactúen, discutan y se alineen en lo que es más importante.',
        },
        instant: {
          title: 'Sin Configuración Necesaria',
          description: 'Juega instantáneamente en línea con tu equipo. Sin descargas, sin configuración complicada—simplemente comienza a jugar.',
        },
      },
      cta: {
        title: '¿Listo para Construir un Mejor Equipo?',
        subtitle: 'Comienza a jugar con tu equipo hoy y descubre qué los motiva realmente.',
        button: 'Explorar Juegos',
      },
    },
    about: {
      hero: {
        title: 'Acerca de Squadron Play',
        tagline: 'Creando el Mejor Hub de Juegos y Colaboración en Equipo',
      },
      story: {
        title: 'Nuestra Historia',
        subtitle: 'Cómo todo comenzó',
        content: [
          'Squadron Play nació de una idea simple: los equipos necesitan mejores formas de colaborar, comunicarse y conectarse. Creíamos que los juegos podrían ser mucho más que entretenimiento—podrían ser una herramienta poderosa para construir relaciones más fuertes y fomentar colaboración significativa.',
          'Nuestros fundadores reconocieron que los ejercicios tradicionales de team building a menudo se sentían forzados e ineficaces. Visualizaron una plataforma donde los juegos se convirtieran en un puente natural entre compañeros de equipo, permitiendo conversaciones auténticas y conexiones genuinas.',
          'Hoy en día, Squadron Play es un testimonio del poder de la colaboración lúdica. Continuamos innovando y creando experiencias que acercan a los equipos, haciendo el trabajo más agradable y los equipos más conectados.',
        ],
      },
      pillars: {
        title: 'La Fundación de Squadron Play',
        subtitle: 'Lo que nos impulsa hacia adelante',
      },
      mission: {
        title: '🎯 Misión',
        description: 'Crear experiencias de juegos atractivas y colaborativas que fortalezcan los lazos del equipo y la comunicación.',
      },
      vision: {
        title: '🚀 Visión',
        description: 'Un mundo donde los equipos se conecten a través de juegos divertidos y significativos que fomenten la colaboración y el crecimiento.',
      },
      values: {
        title: '💎 Valores',
        description: 'Integridad, innovación e inclusividad en todo lo que hacemos.',
      },
      drives: {
        title: 'Lo Que Nos Motiva',
        collaboration: {
          title: '🤝 Colaboración',
          description: 'Creemos que grandes cosas suceden cuando las personas trabajan juntas y realmente se entienden.',
        },
        fun: {
          title: '😊 Diversión',
          description: 'El trabajo debe ser agradable. Creamos experiencias que traen alegría y risas al lugar de trabajo.',
        },
        growth: {
          title: '📈 Crecimiento',
          description: 'Cada juego es una oportunidad para aprender, desarrollar nuevas habilidades y mejorar como equipo.',
        },
        learning: {
          title: '🎓 Aprendizaje',
          description: 'Estamos comprometidos con la mejora continua y ayudar a los equipos a descubrir nuevas formas de éxito juntos.',
        },
      },
    },
    contact: {
      hero: {
        title: 'Ponte en Contacto',
        tagline: 'Nos encantaría saber de ti y responder todas tus preguntas',
      },
      ways: {
        title: 'Formas de Contactarnos',
        subtitle: 'Elige la mejor manera de conectarte con nuestro equipo',
      },
      methods: {
        email: {
          title: 'Envíanos un Correo',
          description: 'Envíanos un correo en cualquier momento',
        },
        support: {
          title: 'Soporte en Vivo',
          description: 'Chatea con nuestro equipo de soporte',
          value: 'Disponible 9 AM - 6 PM EST',
        },
        community: {
          title: 'Únete a la Comunidad',
          description: 'Conéctate con otros usuarios',
          value: 'Discord & Foros',
        },
      },
      form: {
        title: 'Envíanos un Mensaje',
        subtitle: 'Completa el formulario a continuación y te responderemos pronto',
        name: 'Nombre',
        namePlaceholder: 'Tu nombre',
        email: 'Correo',
        emailPlaceholder: 'tu.correo@ejemplo.com',
        message: 'Mensaje',
        messagePlaceholder: 'Cuéntanos cómo podemos ayudarte...',
        submit: 'Enviar Mensaje',
      },
    },
    apps: {
      hero: {
        title: 'Aplicaciones',
        tagline: 'Explora nuestra colección de juegos y herramientas interactivas para equipos',
      },
      available: {
        title: 'Juegos Disponibles',
        subtitle: 'Selecciona un juego para comenzar a jugar con tu equipo',
      },
      title: 'Aplicaciones',
      subtitle: 'Explora nuestra colección de aplicaciones y juegos',
      noApps: 'No hay aplicaciones disponibles en este momento.',
    },
  },
  fr: {
    nav: {
      home: 'Accueil',
      about: 'À propos',
      contact: 'Contact',
      apps: 'Apps',
    },
    home: {
      hero: {
        titleHighlight: 'Squadron Play',
        tagline: 'L\'Ultime Hub de Jeux et Collaboration d\'Équipe',
        subtitle: 'Découvrez des jeux incroyables conçus pour rendre le travail d\'équipe amusant, la prise de décision plus facile et la création d\'équipe mémorable. Pas de téléchargements—entrez simplement, jouez et amusez-vous!',
        exploreApps: 'Commencer à Jouer',
        learnMore: 'En Savoir Plus',
      },
      games: {
        title: '🎮 Découvrez Nos Jeux',
        subtitle: 'Impliquez votre équipe avec des jeux interactifs conçus pour la collaboration et la croissance',
        motivators: {
          title: 'Moving Motivators',
          description: 'Alignez votre équipe autour de ce qui les motive vraiment. Parfait pour comprendre les valeurs d\'équipe, construire la confiance et créer une vision partagée.',
          cta: 'Jouer Moving Motivators',
        },
        poker: {
          title: 'Delegation Poker',
          description: 'Maîtrisez la prise de décision avec un vote attrayant sur qui devrait gérer les décisions. Apprenez qui est responsable de quoi et renforcez l\'autonomie de l\'équipe.',
          cta: 'Jouer Delegation Poker',
        },
      },
      why: {
        title: '✨ Pourquoi Squadron Play?',
        subtitle: 'Tout ce dont vous avez besoin pour construire des équipes plus fortes et alignées',
        engaging: {
          title: 'Attrayant & Amusant',
          description: 'Des jeux véritablement agréables qui rendent la création d\'équipe et l\'apprentissage moins comme du travail et plus comme du plaisir.',
        },
        collaborative: {
          title: 'Axé sur l\'Équipe',
          description: 'Conçu spécifiquement pour que les équipes interagissent, discutent et s\'alignent sur ce qui est plus important.',
        },
        instant: {
          title: 'Aucune Configuration Nécessaire',
          description: 'Jouez instantanément en ligne avec votre équipe. Pas de téléchargements, pas de configuration compliquée—commencez simplement à jouer.',
        },
      },
      cta: {
        title: 'Prêt à Construire une Meilleure Équipe?',
        subtitle: 'Commencez à jouer avec votre équipe aujourd\'hui et découvrez ce qui les motive vraiment.',
        button: 'Explorez les Jeux',
      },
    },
    about: {
      hero: {
        title: 'À propos de Squadron Play',
        tagline: 'Créer le Plus Grand Hub de Jeux et de Collaboration d\'Équipe',
      },
      story: {
        title: 'Notre Histoire',
        subtitle: 'Comment tout a commencé',
        content: [
          'Squadron Play est né d\'une idée simple : les équipes ont besoin de meilleures façons de collaborer, communiquer et se connecter. Nous croyions que les jeux pourraient être bien plus que du simple divertissement—ils pourraient être un outil puissant pour construire des relations plus fortes et favoriser une collaboration significative.',
          'Nos fondateurs ont reconnu que les exercices traditionnels de team building semblaient souvent forcés et inefficaces. Ils ont envisagé une plateforme où les jeux deviendraient un pont naturel entre les coéquipiers, permettant des conversations authentiques et des connexions véritables.',
          'Aujourd\'hui, Squadron Play témoigne du pouvoir de la collaboration ludique. Nous continuons à innover et créer des expériences qui rapprochent les équipes, rendant le travail plus agréable et les équipes plus connectées.',
        ],
      },
      pillars: {
        title: 'La Fondation de Squadron Play',
        subtitle: 'Ce qui nous pousse à avancer',
      },
      mission: {
        title: '🎯 Mission',
        description: 'Créer des expériences de jeux engageantes et collaboratives qui renforcent les liens d\'équipe et la communication.',
      },
      vision: {
        title: '🚀 Vision',
        description: 'Un monde où les équipes se connectent à travers des jeux amusants et significatifs qui favorisent la collaboration et la croissance.',
      },
      values: {
        title: '💎 Valeurs',
        description: 'L\'intégrité, l\'innovation et l\'inclusivité dans tout ce que nous faisons.',
      },
      drives: {
        title: 'Ce Qui Nous Motive',
        collaboration: {
          title: '🤝 Collaboration',
          description: 'Nous croyons que de grandes choses se produisent quand les gens travaillent ensemble et se comprennent vraiment.',
        },
        fun: {
          title: '😊 Amusement',
          description: 'Le travail devrait être agréable. Nous créons des expériences qui apportent de la joie et du rire au lieu de travail.',
        },
        growth: {
          title: '📈 Croissance',
          description: 'Chaque jeu est une opportunité d\'apprendre, de développer de nouvelles compétences et de s\'améliorer en tant qu\'équipe.',
        },
        learning: {
          title: '🎓 Apprentissage',
          description: 'Nous sommes engagés par l\'amélioration continue et aidons les équipes à découvrir de nouvelles façons de réussir ensemble.',
        },
      },
    },
    contact: {
      hero: {
        title: 'Contactez-nous',
        tagline: 'Nous aimerions avoir de vos nouvelles et répondre à toutes vos questions',
      },
      ways: {
        title: 'Façons de Nous Contacter',
        subtitle: 'Choisissez la meilleure façon de vous connecter avec notre équipe',
      },
      methods: {
        email: {
          title: 'Envoyez-nous un E-mail',
          description: 'Envoyez-nous un e-mail à tout moment',
        },
        support: {
          title: 'Support en Direct',
          description: 'Discutez avec notre équipe de support',
          value: 'Disponible 9h - 18h EST',
        },
        community: {
          title: 'Rejoignez la Communauté',
          description: 'Connectez-vous avec d\'autres utilisateurs',
          value: 'Discord & Forums',
        },
      },
      form: {
        title: 'Envoyez-nous un Message',
        subtitle: 'Remplissez le formulaire ci-dessous et nous vous répondrons rapidement',
        name: 'Nom',
        namePlaceholder: 'Votre nom',
        email: 'E-mail',
        emailPlaceholder: 'votre.email@exemple.com',
        message: 'Message',
        messagePlaceholder: 'Dites-nous comment nous pouvons vous aider...',
        submit: 'Envoyer Message',
      },
    },
    apps: {
      hero: {
        title: 'Applications',
        tagline: 'Explorez notre collection de jeux et outils interactifs pour équipes',
      },
      available: {
        title: 'Jeux Disponibles',
        subtitle: 'Sélectionnez un jeu pour commencer à jouer avec votre équipe',
      },
      title: 'Applications',
      subtitle: 'Explorez notre collection d\'applications et jeux',
      noApps: 'Aucune application disponible pour le moment.',
    },
  },
  de: {
    nav: {
      home: 'Startseite',
      about: 'Über uns',
      contact: 'Kontakt',
      apps: 'Apps',
    },
    home: {
      hero: {
        titleHighlight: 'Squadron Play',
        tagline: 'Der Ultimative Gaming- und Team-Collaboration-Hub',
        subtitle: 'Erleben Sie unglaubliche Spiele, die zum Zusammenarbeiten im Team einladen, die Entscheidungsfindung einfacher machen und die Teambildung unvergesslich gestalten. Keine Downloads nötig—einfach einsteigen, spielen und Spaß haben!',
        exploreApps: 'Jetzt Spielen',
        learnMore: 'Mehr Erfahren',
      },
      games: {
        title: '🎮 Entdecken Sie Unsere Spiele',
        subtitle: 'Binden Sie Ihr Team mit interaktiven Spielen ein, die für Zusammenarbeit und Wachstum konzipiert sind',
        motivators: {
          title: 'Moving Motivators',
          description: 'Richten Sie Ihr Team auf das ab, was sie wirklich motiviert. Perfekt, um Teamwerte zu verstehen, Vertrauen aufzubauen und eine gemeinsame Vision zu schaffen.',
          cta: 'Moving Motivators Spielen',
        },
        poker: {
          title: 'Delegation Poker',
          description: 'Beherrschen Sie Entscheidungsfindung mit ansprechenden Abstimmungen darüber, wer für Entscheidungen verantwortlich sein sollte. Erfahren Sie, wer für was zuständig ist und fördern Sie Team-Autonomie.',
          cta: 'Delegation Poker Spielen',
        },
      },
      why: {
        title: '✨ Warum Squadron Play?',
        subtitle: 'Alles was Sie brauchen, um stärkere und besser abgestimmte Teams aufzubauen',
        engaging: {
          title: 'Ansprechend & Spaßig',
          description: 'Echt angenehme Spiele, die Teambildung und Lernen weniger wie Arbeit und mehr wie Freude erscheinen lassen.',
        },
        collaborative: {
          title: 'Team-Fokussiert',
          description: 'Speziell dafür konzipiert, dass Teams interagieren, diskutieren und sich auf das Wichtigste abstimmen.',
        },
        instant: {
          title: 'Keine Einrichtung Erforderlich',
          description: 'Spielen Sie sofort online mit Ihrem Team. Keine Downloads, keine komplizierte Einrichtung—einfach spielen.',
        },
      },
      cta: {
        title: 'Bereit für ein Besseres Team?',
        subtitle: 'Beginnen Sie heute, mit Ihrem Team zu spielen, und entdecken Sie, was Sie wirklich motiviert.',
        button: 'Erkunden Sie die Spiele',
      },
    },
    about: {
      hero: {
        title: 'Über Squadron Play',
        tagline: 'Schaffung des Ultimativen Gaming- und Team-Collaboration-Hubs',
      },
      story: {
        title: 'Unsere Geschichte',
        subtitle: 'Wie alles begann',
        content: [
          'Squadron Play entstand aus einer einfachen Idee: Teams brauchen bessere Wege, um zusammenzuarbeiten, zu kommunizieren und sich zu verbinden. Wir glaubten, dass Spiele viel mehr als nur Unterhaltung sein könnten—sie könnten ein leistungsstarkes Werkzeug für den Aufbau stärkerer Beziehungen und die Förderung sinnvoller Zusammenarbeit sein.',
          'Unsere Gründer erkannten, dass traditionelle Team-Building-Übungen sich oft erzwungen und ineffektiv anfühlten. Sie stellten sich eine Plattform vor, auf der Spiele zur natürlichen Brücke zwischen Teamkollegen werden und echte Gespräche sowie echte Verbindungen ermöglichen.',
          'Heute ist Squadron Play ein Beweis für die Kraft des spielerischen Zusammenarbeit. Wir setzen unsere Innovation fort und schaffen Erlebnisse, die Teams zusammenbringen, Arbeit angenehmer machen und Teams stärker verbinden.',
        ],
      },
      pillars: {
        title: 'Das Fundament von Squadron Play',
        subtitle: 'Was uns vorantreibt',
      },
      mission: {
        title: '🎯 Mission',
        description: 'Schaffen Sie ansprechende und kollaborative Spielerlebnisse, die die Teamfähigkeit und Kommunikation stärken.',
      },
      vision: {
        title: '🚀 Vision',
        description: 'Eine Welt, in der sich Teams durch spaßige, bedeutungsvolle Spiele verbinden, die Zusammenarbeit und Wachstum fördern.',
      },
      values: {
        title: '💎 Werte',
        description: 'Integrität, Innovation und Inklusivität in allem, was wir tun.',
      },
      drives: {
        title: 'Was Uns Antreibt',
        collaboration: {
          title: '🤝 Zusammenarbeit',
          description: 'Wir glauben, dass großartige Dinge passieren, wenn Menschen zusammenarbeiten und sich wirklich verstehen.',
        },
        fun: {
          title: '😊 Spaß',
          description: 'Arbeit sollte angenehm sein. Wir schaffen Erlebnisse, die Freude und Lachen am Arbeitsplatz bringen.',
        },
        growth: {
          title: '📈 Wachstum',
          description: 'Jedes Spiel ist eine Gelegenheit zu lernen, neue Fähigkeiten zu entwickeln und sich als Team zu verbessern.',
        },
        learning: {
          title: '🎓 Lernen',
          description: 'Wir sind verpflichtet zur kontinuierlichen Verbesserung und helfen Teams, neue Wege zum gemeinsamen Erfolg zu entdecken.',
        },
      },
    },
    contact: {
      hero: {
        title: 'Kontakt Aufnehmen',
        tagline: 'Wir würden gerne von Ihnen hören und alle Ihre Fragen beantworten',
      },
      ways: {
        title: 'Wege, Uns zu Erreichen',
        subtitle: 'Wählen Sie den besten Weg, um sich mit unserem Team zu verbinden',
      },
      methods: {
        email: {
          title: 'Senden Sie Uns eine E-Mail',
          description: 'Senden Sie uns jederzeit eine E-Mail',
        },
        support: {
          title: 'Live-Support',
          description: 'Chatten Sie mit unserem Support-Team',
          value: 'Verfügbar 9 - 18 Uhr MEZ',
        },
        community: {
          title: 'Der Community Beitreten',
          description: 'Verbinden Sie sich mit anderen Benutzern',
          value: 'Discord & Foren',
        },
      },
      form: {
        title: 'Senden Sie Uns eine Nachricht',
        subtitle: 'Füllen Sie das Formular unten aus und wir melden uns bald bei Ihnen',
        name: 'Name',
        namePlaceholder: 'Ihr Name',
        email: 'E-Mail',
        emailPlaceholder: 'ihre.email@beispiel.com',
        message: 'Nachricht',
        messagePlaceholder: 'Sagen Sie uns, wie wir helfen können...',
        submit: 'Nachricht Senden',
      },
    },
    apps: {
      hero: {
        title: 'Anwendungen',
        tagline: 'Erkunden Sie unsere Sammlung interaktiver Spiele und Tools für Teams',
      },
      available: {
        title: 'Verfügbare Spiele',
        subtitle: 'Wählen Sie ein Spiel, um mit Ihrem Team zu spielen',
      },
      title: 'Anwendungen',
      subtitle: 'Durchsuchen Sie unsere Sammlung von Apps und Spielen',
      noApps: 'Derzeit sind keine Anwendungen verfügbar.',
    },
  },
};

export const getTranslation = (language: Language): Translations => {
  return translations[language];
};
